import { useAskRedoSlidePanel } from "@redotech/redo-merchant-app-common/ask-redo/ask-redo-slide-panel-context";
import { useAskRedoEnabled } from "@redotech/redo-merchant-app-common/ask-redo/use-ask-redo-enabled";
import { ClientUploader } from "@redotech/redo-merchant-app-common/client/upload";
import { GlobalAgentContext } from "@redotech/redo-merchant-app-common/global-agent-context";
import { AiSlidePanelLayout } from "@redotech/redo-web/ai/chat/components/ai-slide-panel-layout";
import {
  createContext,
  lazy,
  memo,
  ReactNode,
  Suspense,
  useContext,
  useEffect,
} from "react";
import { AskRedoEscalationWatcher } from "../escalation/ask-redo-escalation-watcher";
import { EscalationAuthorProvider } from "../escalation/escalation-author-context";

/**
 * Wraps the merchant app shell with the Ask Redo slide-out column.
 *
 * Two responsibilities:
 *
 *   1. **Flag gate.** When the feature is off, render children
 *      verbatim — no wrapper div, no CSS grid, no `overflow:auto`
 *      sandwich. The dormant code path adds zero DOM cost.
 *
 *   2. **Bundle gate (panel-only).** When the feature is on, the
 *      heavy bit is the slide-out content (it transitively imports
 *      the AI SDK, transport, and every chat component). We
 *      `React.lazy()` that — but ONLY the panel slot, not the grid
 *      around the merchant content. Wrapping `children` inside a
 *      Suspense boundary would mean every route's state resets when
 *      the chunk lands, because React treats fallback children and
 *      resolved children as separate subtrees. Wrapping just the
 *      panel keeps `children` mounted at the same identity through
 *      the load.
 *
 * The grid + resize / notification-offset / focus-on-open behaviour
 * comes from the shared `AiSlidePanelLayout` primitive (also used by
 * the legacy `GlobalAI` and onboarding's `GlobalAskRedo`). We drive
 * its `isOpen` from our own context so the new panel's state is
 * isolated from the legacy one's.
 */
const LazyAskRedoSlideOut = lazy(() =>
  import("../slide-out/ask-redo-slide-out").then((m) => ({
    default: m.AskRedoSlideOut,
  })),
);

export interface AskRedoOutletPanelContextValue {
  panel: ReactNode;
  attentionSignal: number;
}

export const AskRedoOutletPanelContext = createContext<
  AskRedoOutletPanelContextValue | undefined
>(undefined);

export const AskRedoLayout = memo(function AskRedoLayout({
  children,
}: {
  children: ReactNode;
}) {
  const { enabled } = useAskRedoEnabled();
  const { close, isOpen, open } = useAskRedoSlidePanel();
  const { askRedoAttentionSignal, askRedoViewMode } =
    useContext(GlobalAgentContext);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (askRedoViewMode !== "page-takeover") {
      return;
    }
    open();
    return () => close();
  }, [askRedoViewMode, close, enabled, open]);

  if (!enabled) {
    return <>{children}</>;
  }

  const askRedoPanel = (
    <Suspense fallback={null}>
      <LazyAskRedoSlideOut
        variant={
          askRedoViewMode === "page-takeover" ? "page-takeover" : "slide-panel"
        }
      />
    </Suspense>
  );

  // `ClientUploader` provides `UploaderContext` so the prompt
  // input can offer image/file uploads. The legacy chat mounts its
  // own ClientUploader inside `GlobalAI`; we mount one here so the
  // Ask Redo slide-out + full page have the uploader available
  // even when the legacy panel is collapsed. Nested providers are
  // fine — the closest one wins for any given consumer.
  // `EscalationAuthorProvider` wraps BOTH the watcher (which writes
  // agent name/avatar as SSE events arrive) and the slide-out
  // (which reads them when rendering the banner + human message
  // bubbles). Mounting it here means the agent identity persists
  // across slide-out close/reopen cycles — the slide-out unmounts
  // freely, but the captured author info survives because the
  // provider lives at the layout level.
  return (
    <ClientUploader>
      <EscalationAuthorProvider>
        <AskRedoEscalationWatcher />
        {askRedoViewMode === "page-takeover" && isOpen ? (
          <AskRedoOutletPanelContext.Provider
            value={{
              panel: askRedoPanel,
              attentionSignal: askRedoAttentionSignal,
            }}
          >
            {children}
          </AskRedoOutletPanelContext.Provider>
        ) : (
          <AskRedoOutletPanelContext.Provider value={undefined}>
            <AiSlidePanelLayout isOpen={isOpen} panel={askRedoPanel}>
              {children}
            </AiSlidePanelLayout>
          </AskRedoOutletPanelContext.Provider>
        )}
      </EscalationAuthorProvider>
    </ClientUploader>
  );
});
